import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { budget, type MonthSummary } from '../lib/budget';
import { useAuth } from '../lib/auth-context';
import { useLiveReload } from '../lib/live-context';
import { formatCents, monthLabel, shortDate } from '../lib/money';
import { todos, type Todo } from '../lib/todos';

export function Dashboard() {
  const { state } = useAuth();
  const [summary, setSummary] = useState<MonthSummary | null>(null);
  const [openTodos, setOpenTodos] = useState<Todo[]>([]);
  const [error, setError] = useState<string | null>(null);

  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;

  const load = useCallback(() => {
    return Promise.all([budget.month(year, month), todos.list({ status: 'open' })])
      .then(([monthSummary, list]) => {
        setSummary(monthSummary);
        setOpenTodos(list.todos.slice(0, 5));
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not load.'));
  }, [year, month]);

  useEffect(() => {
    void load();
  }, [load]);

  useLiveReload(() => {
    void load();
  });

  if (state.status !== 'signedIn') return null;
  const { user, partner } = state.me;

  const over = summary?.categories.filter((c) => c.overBudget) ?? [];
  const noCategories = summary !== null && summary.categories.length === 0;

  return (
    <main className="page">
      <h1>Hi {user.displayName}</h1>
      <p className="hint">
        {monthLabel(year, month)}
        {partner ? ` · shared with ${partner.displayName}` : ''}
      </p>

      {error && <p className="error">{error}</p>}

      {noCategories ? (
        <section className="panel">
          <h2>Get started</h2>
          <p className="hint">
            Add a few spending categories with monthly limits, then start recording expenses.
          </p>
          <Link className="secondary block" to="/categories">
            Set up categories
          </Link>
        </section>
      ) : (
        summary && (
          <>
            <section className="totals big-totals">
              <div>
                <span className="meta">Spent</span>
                <strong>{formatCents(summary.totals.spentCents)}</strong>
              </div>
              <div>
                <span className="meta">Left this month</span>
                <strong className={summary.totals.remainingCents < 0 ? 'over' : 'under'}>
                  {formatCents(summary.totals.remainingCents)}
                </strong>
              </div>
            </section>

            {over.length > 0 && (
              <section className="panel">
                <h2>Over budget</h2>
                <ul className="list">
                  {over.map((c) => (
                    <li key={c.id}>
                      <strong>{c.name}</strong>
                      <span className="over">{formatCents(-c.remainingCents)} over</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <Link className="primary block" to="/budget">
              Open budget
            </Link>
          </>
        )
      )}

      {openTodos.length > 0 ? (
        <section className="panel">
          <h2>Open todos</h2>
          <ul className="list">
            {openTodos.map((t) => (
              <li key={t.id}>
                <div>
                  <strong className={t.overdue ? 'over' : undefined}>{t.title}</strong>
                  <span className="meta">
                    {t.assignedTo ? t.assignedTo.displayName : 'Anyone'}
                    {t.dueDate ? ` · ${shortDate(t.dueDate)}` : ''}
                  </span>
                </div>
              </li>
            ))}
          </ul>
          <Link className="link block-link" to="/todos">
            All todos
          </Link>
        </section>
      ) : (
        <Link className="secondary block" to="/todos">
          Todos
        </Link>
      )}
    </main>
  );
}
